import {
  FormattedHTMLMessage,
  FormattedMessage,
  InjectedIntl,
  injectIntl,
} from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
  CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';

import { LoadingStatus } from 'src/constants/loading-status';
import {
  closeProjectsMenu,
  fetchJiraRelevantProjects,
  openManageProjectsDialog,
  openProjectsMenu,
} from 'src/redux/jira/actions';
import {
  getIsProjectsMenuOpen,
  getProjectAssociations,
  getRelevantProjectFetchedStatus,
} from 'src/redux/jira/selectors/repo-page-selectors';
import { getRepositoryDetails } from 'src/selectors/repository-selectors';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { SCREEN_NAME } from '../constants';
import { ErrorMessageWithRetry } from '../error-message-with-retry';
import { JiraProjectCard } from '../jira-project-card';

import relevantProjectSvgSelectedState from './project-icon-white.svg';
import relevantProjectSvgDefaultState from './project-icon.svg';
import messages from './project-menu.i18n';
import * as styles from './project-menu.styled';

type Props = {
  intl: InjectedIntl;
};

const ProjectMenu = ({ intl }: Props) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(getIsProjectsMenuOpen);
  const relevantProjectsFetchedStatus = useSelector(
    getRelevantProjectFetchedStatus
  );
  const projectAssociations = useSelector(getProjectAssociations);
  const repositoryDetails = useSelector(getRepositoryDetails);

  const onProjectMenuOpenChange = (attrs: { isOpen: boolean }) => {
    if (attrs.isOpen) {
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'dropdown',
        actionSubjectId: 'relevantProjectList',
        source: SCREEN_NAME,
        attributes: {
          projectsCount: projectAssociations.length,
        },
      });

      dispatch(openProjectsMenu());
    } else {
      dispatch(closeProjectsMenu());
    }
  };

  const onProjectLinkClick = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'openProjectButton',
      source: SCREEN_NAME,
    });
  };

  const onProjectManagementItemClick = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'projectManagementButton',
      source: SCREEN_NAME,
    });

    dispatch(openManageProjectsDialog());
  };

  const projects = projectAssociations
    .map(association => association.project)
    .sort((a, b) => a.name.localeCompare(b.name));

  const label = intl.formatMessage(messages.tooltipText);

  const isLoading = ![
    LoadingStatus.Success,
    LoadingStatus.Failed,
    LoadingStatus.Forbidden,
  ].includes(relevantProjectsFetchedStatus);
  const isFailed = relevantProjectsFetchedStatus === LoadingStatus.Failed;
  const noRelevantProjects =
    relevantProjectsFetchedStatus === LoadingStatus.Success &&
    projects.length === 0;
  const noAccessibleRelevantProjects =
    relevantProjectsFetchedStatus === LoadingStatus.Forbidden;

  const noRelevantProjectsMessage = () => {
    if (noRelevantProjects || noAccessibleRelevantProjects) {
      return (
        <>
          <styles.NoRelevantProjectMessage>
            <FormattedMessage {...messages.noLinkedProjectsMessage} />
          </styles.NoRelevantProjectMessage>
          <styles.NoRelevantProjectMessage>
            <FormattedMessage {...messages.linkProjectHelperMessage} />
          </styles.NoRelevantProjectMessage>
        </>
      );
    }
    return null;
  };

  const Trigger = ({ triggerRef, ...props }: CustomTriggerProps) => (
    <Tooltip content={label} position="top" hideTooltipOnClick>
      <styles.TriggerButton
        {...props}
        ref={triggerRef}
        appearance="default"
        iconBefore={
          <img
            alt={label}
            src={
              isOpen
                ? relevantProjectSvgSelectedState
                : relevantProjectSvgDefaultState
            }
            data-testid="repo-jira-tab-project-switcher"
          />
        }
        iconAfter={<ChevronDownIcon label={label} />}
        isDisabled={isLoading}
        isLoading={isLoading}
        isSelected={isOpen}
      />
    </Tooltip>
  );

  const manageProjectsItem = () => {
    const renderItem = (isEnabled: boolean) => (
      <styles.ManageProjectsItem>
        <DropdownItemGroup>
          <DropdownItem
            elemBefore={<SettingsIcon label="" />}
            onClick={onProjectManagementItemClick}
            data-testid="repo-jira-tab-manage-projects-action"
            isDisabled={!isEnabled}
          >
            {intl.formatMessage(messages.manageLinkedProjectsButtonLabel)}
          </DropdownItem>
        </DropdownItemGroup>
      </styles.ManageProjectsItem>
    );

    if (['write', 'admin'].includes(repositoryDetails.accessLevel || '')) {
      return renderItem(true);
    } else {
      const tooltip = (
        <FormattedHTMLMessage
          {...messages.manageLinkedProjectsNotAllowedTooltipText}
        />
      );
      return <Tooltip content={tooltip}>{renderItem(false)}</Tooltip>;
    }
  };

  return (
    <DropdownMenu
      trigger={Trigger}
      placement="bottom-end"
      onOpenChange={onProjectMenuOpenChange}
      isOpen={isOpen}
    >
      <styles.DropdownInnerWrapper>
        <DropdownItemGroup
          title={intl.formatMessage(messages.groupHeaderLabel)}
        >
          <styles.DropdownGroupProjects>
            {projects.length > 0 &&
              projects.map((project, i) => (
                <DropdownItem key={i} onClick={onProjectLinkClick}>
                  <JiraProjectCard project={project} />
                </DropdownItem>
              ))}

            {isLoading && (
              <styles.DropdownMessage>
                <Spinner size="medium" />
              </styles.DropdownMessage>
            )}
            {isFailed && (
              <styles.DropdownMessage>
                <ErrorMessageWithRetry
                  onRetry={() => dispatch(fetchJiraRelevantProjects())}
                  errorMessage={intl.formatMessage(
                    messages.errorLoadingMessage
                  )}
                />
              </styles.DropdownMessage>
            )}
            {noRelevantProjectsMessage()}
          </styles.DropdownGroupProjects>
        </DropdownItemGroup>
        {manageProjectsItem()}
      </styles.DropdownInnerWrapper>
    </DropdownMenu>
  );
};

export default injectIntl(ProjectMenu);
